import { useState, useEffect, useCallback } from 'react';
import * as apiService from '../services/apiService';
import * as sessionStorageService from '../services/sessionStorageService';
import { build_dynamic_system_prompt } from '../utils/build_dynamic_system_prompt';
import logger from '../utils/logger';

/**
 * Custom hook for managing profile data with API integration and sessionStorage persistence
 * Follows the established pattern from useChatHistory.js
 */
const useProfileData = () => {
  const [profileData, setProfileData] = useState({
    firstname: '',
    lastname: '',
    gender: 'Not Specified',
    form_of_address: '',
    personal_info: [],
    learned_information: []
  });
  const [isEditing, setIsEditing] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  /**
   * Organize facts by type for efficient frontend consumption
   * @param {Array} facts - Array of facts from API
   * @returns {Object} Organized facts by type
   */
  const organizeFactsByType = useCallback((facts) => {
    const organized = {
      firstname: '',
      lastname: '',
      gender: 'Not Specified',
      form_of_address: '',
      personal_info: [],
      learned_information: []
    };

    facts.forEach(fact => {
      switch (fact.type) {
        case 'firstname':
          organized.firstname = fact.fact;
          break;
        case 'lastname':
          organized.lastname = fact.fact;
          break;
        case 'gender':
          organized.gender = fact.fact;
          break;
        case 'form_of_address':
          organized.form_of_address = fact.fact;
          break;
        case 'personal_info':
          if (fact.source === 'agent') {
            organized.learned_information.push(fact);
          } else {
            organized.personal_info.push(fact);
          }
          break;
        default:
          logger.warn('Unknown profile fact type', { type: fact.type });
      }
    });

    return organized;
  }, []);

  /**
   * Load profile data from sessionStorage or API
   */
  const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const accessToken = sessionStorageService.getAccessToken();
      if (!accessToken) {
        logger.warn('No access token available for profile data loading');
        return;
      }

      const sessionData = sessionStorageService.getProfileData();
      
      // Check if we have meaningful data in sessionStorage
      if (sessionData.firstname || sessionData.lastname || sessionData.personal_info.length > 0 || (sessionData.learned_information && sessionData.learned_information.length > 0)) {
        setProfileData(sessionData);
        setHasUnsavedChanges(sessionData.unsaved_changes);
        setIsEditing(sessionData.editing_fact_id);
        logger.info('Profile data loaded from sessionStorage', sessionData);
      } else {
        // Load from API
        logger.info('Loading profile data from API');
        const response = await apiService.getProfileFacts(accessToken);
        
        if (response.facts) {
          const organizedData = organizeFactsByType(response.facts);
          setProfileData(organizedData);
          
          // Update sessionStorage
          Object.keys(organizedData).forEach(key => {
            sessionStorageService.setProfileFact(key, organizedData[key]);
          });
          
          logger.info('Profile data loaded from API', organizedData);
        } else {
          logger.warn('Failed to load profile data from API', response);
          setError(response.message || 'Failed to load profile data');
        }
      }
    } catch (error) {
      logger.error('Failed to load profile data', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  }, [organizeFactsByType]);

  /**
   * Update a profile fact and mark as unsaved
   * @param {string} type - Type of fact to update
   * @param {string|Array} value - New value
   */
  const updateProfileFact = useCallback((type, value) => {
    setProfileData(prev => ({ ...prev, [type]: value }));
    sessionStorageService.setProfileFact(type, value);
    setHasUnsavedChanges(true);
    sessionStorageService.setProfileUnsavedChanges(true);
    logger.debug('Profile fact updated', { type, value });
  }, []);

  /**
   * Generate dynamic form of address options based on current profile data
   * @returns {Array} Array of address options
   */
  const generateAddressOptions = useCallback(() => {
    const options = [];
    
    // Add gender-appropriate base options
    if (profileData.gender === 'Male') {
      options.push('Sir');
    } else if (profileData.gender === 'Female') {
      options.push('Madam');
    } else {
      // For non-specified or other genders, include both
      options.push('Sir', 'Madam');
    }
    
    if (profileData.firstname) {
      options.push(profileData.firstname);
    }
    
    if (profileData.lastname) {
      if (profileData.gender === 'Male') {
        options.push(`Mr. ${profileData.lastname}`, `Dr. ${profileData.lastname}`);
      } else if (profileData.gender === 'Female') {
        options.push(`Mrs. ${profileData.lastname}`, `Miss ${profileData.lastname}`, 
                     `Ms. ${profileData.lastname}`, `Dr. ${profileData.lastname}`);
      } else {
        // Gender neutral options
        options.push(`Mr. ${profileData.lastname}`, `Ms. ${profileData.lastname}`, `Dr. ${profileData.lastname}`);
      }
    }
    
    return options;
  }, [profileData.firstname, profileData.lastname, profileData.gender]);

  /**
   * Save all profile changes to the backend
   */
  const saveProfileChanges = useCallback(async () => {
    try {
      console.log('saveProfileChanges called with profileData:', profileData);
      setLoading(true);
      setError(null);
      
      const accessToken = sessionStorageService.getAccessToken();
      if (!accessToken) {
        logger.warn('No access token available for saving profile changes');
        setError('Authentication required');
        return false;
      }

      console.log('Getting current facts from API...');
      // Get current facts from API to determine what needs to be created/updated
      const currentResponse = await apiService.getProfileFacts(accessToken);
      console.log('Current response:', currentResponse);
      if (!currentResponse.facts) {
        setError('Failed to load current profile data');
        return false;
      }

      const currentFacts = currentResponse.facts || [];
      const factMap = new Map();
      currentFacts.forEach(fact => {
        factMap.set(fact.type, fact);
      });

      let allSuccessful = true;

      // Process each profile field
      const fieldsToProcess = [
        { type: 'firstname', value: profileData.firstname },
        { type: 'lastname', value: profileData.lastname },
        { type: 'gender', value: profileData.gender },
        { type: 'form_of_address', value: profileData.form_of_address }
      ];

      for (const field of fieldsToProcess) {
        console.log(`Processing field: ${field.type} = "${field.value}"`);
        if (field.value && field.value.trim()) {
          const existingFact = factMap.get(field.type);
          console.log(`Existing fact for ${field.type}:`, existingFact);
          
          if (existingFact) {
            // Update existing fact
            console.log(`Updating existing fact ${field.type}`);
            const updateResponse = await apiService.updateProfileFact(existingFact.id, field.value, accessToken);
            console.log(`Update response:`, updateResponse);
            if (!updateResponse.id) {
              logger.error('Failed to update profile fact', { type: field.type, error: updateResponse.message });
              allSuccessful = false;
            }
          } else {
            // Create new fact
            console.log(`Creating new fact ${field.type}`);
            const createResponse = await apiService.createProfileFact(field.type, field.value, accessToken);
            console.log(`Create response:`, createResponse);
            if (!createResponse.id) {
              logger.error('Failed to create profile fact', { type: field.type, error: createResponse.message });
              allSuccessful = false;
            }
          }
        } else {
          console.log(`Skipping empty field: ${field.type}`);
        }
      }

      // Process personal_info facts - handle deletions, updates, and creations
      const currentPersonalFacts = currentFacts.filter(fact => fact.type === 'personal_info' && fact.source !== 'agent');
      const currentLearnedFacts = currentFacts.filter(fact => fact.type === 'personal_info' && fact.source === 'agent');
      const frontendPersonalFacts = profileData.personal_info.filter(fact => fact.fact && fact.fact.trim());
      const frontendLearnedFacts = profileData.learned_information.filter(fact => fact.fact && fact.fact.trim());
      
      // Create maps for comparison
      const currentPersonalMap = new Map();
      currentPersonalFacts.forEach(fact => {
        currentPersonalMap.set(fact.id, fact);
      });
      const currentLearnedMap = new Map();
      currentLearnedFacts.forEach(fact => {
        currentLearnedMap.set(fact.id, fact);
      });
      
      const frontendPersonalMap = new Map();
      frontendPersonalFacts.forEach(fact => {
        const factId = fact.id || fact._id;
        if (factId) {
          frontendPersonalMap.set(factId, fact);
        }
      });
      const frontendLearnedMap = new Map();
      frontendLearnedFacts.forEach(fact => {
        const factId = fact.id || fact._id;
        if (factId) {
          frontendLearnedMap.set(factId, fact);
        }
      });
      
      // Delete facts that exist in MongoDB but not in frontend
      for (const [factId] of currentPersonalMap) {
        if (!frontendPersonalMap.has(factId)) {
          console.log(`Deleting personal fact ${factId}`);
          const deleteResponse = await apiService.deleteProfileFact(factId, accessToken);
          // Check for successful deletion - API returns success message in response
          if (deleteResponse.error && deleteResponse.error.includes('deleted successfully')) {
            logger.info('Personal fact deleted successfully', { factId });
          } else if (deleteResponse.success === false) {
            logger.error('Failed to delete personal fact', { factId: factId, error: deleteResponse.message || deleteResponse.error });
            allSuccessful = false;
          }
        }
      }
      // Delete learned facts that exist in MongoDB but not in frontend
      for (const [factId] of currentLearnedMap) {
        if (!frontendLearnedMap.has(factId)) {
          console.log(`Deleting learned information fact ${factId}`);
          const deleteResponse = await apiService.deleteProfileFact(factId, accessToken);
          if (deleteResponse.success === false) {
            logger.error('Failed to delete learned information fact', { factId: factId, error: deleteResponse.message || deleteResponse.error });
            allSuccessful = false;
          }
        }
      }
      
      // Update/create facts that exist in frontend
      for (const personalFact of frontendPersonalFacts) {
        if (personalFact.id || personalFact._id) {
          // Update existing personal fact
          const factId = personalFact.id || personalFact._id;
          const updateResponse = await apiService.updateProfileFact(factId, personalFact.fact, accessToken);
          if (!updateResponse.id) {
            logger.error('Failed to update personal fact', { factId: factId, error: updateResponse.message });
            allSuccessful = false;
          }
        } else {
          // Create new personal fact
          const createResponse = await apiService.createProfileFact('personal_info', personalFact.fact, accessToken);
          if (!createResponse.id) {
            logger.error('Failed to create personal fact', { fact: personalFact.fact, error: createResponse.message });
            allSuccessful = false;
          }
        }
      }

      // Update learned information facts (no creation here; users cannot add learned facts)
      for (const learnedFact of frontendLearnedFacts) {
        if (learnedFact.id || learnedFact._id) {
          const factId = learnedFact.id || learnedFact._id;
          const updateResponse = await apiService.updateProfileFact(factId, learnedFact.fact, accessToken);
          if (!updateResponse.id) {
            logger.error('Failed to update learned information fact', { factId: factId, error: updateResponse.message });
            allSuccessful = false;
          }
        }
      }

      if (allSuccessful) {
        setHasUnsavedChanges(false);
        sessionStorageService.setProfileUnsavedChanges(false);
        setSuccessMessage('Profile changes saved successfully!');
        setError(null);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
        
        logger.info('Profile changes saved successfully');
        
        // Rebuild dynamic system prompt with updated profile data
        try {
          logger.info('Rebuilding dynamic system prompt after profile changes');
          build_dynamic_system_prompt();
        } catch (error) {
          logger.error('Failed to rebuild dynamic system prompt after profile changes', { error: error.message });
          // Non-fatal error - profile save was successful
        }
        
        return true;
      } else {
        setError('Some profile changes failed to save');
        setSuccessMessage(null);
        return false;
      }
    } catch (error) {
      logger.error('Failed to save profile changes', error);
      setError('Failed to save profile changes');
      setSuccessMessage(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [profileData]);

  /**
   * Undo unsaved changes by reloading fresh data from API
   */
  const undoChanges = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      const accessToken = sessionStorageService.getAccessToken();
      if (!accessToken) {
        logger.warn('No access token available for undoing changes');
        setError('Authentication required');
        return;
      }

      // Reload fresh data from API
      logger.info('Reloading profile data from API to undo changes');
      const response = await apiService.getProfileFacts(accessToken);
      
      if (response.facts) {
        const organizedData = organizeFactsByType(response.facts);
        setProfileData(organizedData);
        
        // Update sessionStorage with fresh data
        Object.keys(organizedData).forEach(key => {
          sessionStorageService.setProfileFact(key, organizedData[key]);
        });
        
        setHasUnsavedChanges(false);
        sessionStorageService.setProfileUnsavedChanges(false);
        
        logger.info('Profile changes undone - data reloaded from API', organizedData);
      } else {
        logger.warn('Failed to reload profile data from API', response);
        setError(response.message || 'Failed to reload profile data');
      }
    } catch (error) {
      logger.error('Failed to undo profile changes', error);
      setError('Failed to undo profile changes');
    } finally {
      setLoading(false);
    }
  }, [organizeFactsByType]);

  /**
   * Add a new personal info fact
   * @param {string} fact - Fact content
   */
  const addPersonalInfoFact = useCallback((fact) => {
    if (fact && fact.trim()) {
      const newFact = {
        fact: fact.trim(),
        type: 'personal_info',
        id: null // Will be set when saved to backend
      };
      
      setProfileData(prev => ({
        ...prev,
        personal_info: [...prev.personal_info, newFact]
      }));
      
      sessionStorageService.setProfileFact('personal_info', [...profileData.personal_info, newFact]);
      setHasUnsavedChanges(true);
      sessionStorageService.setProfileUnsavedChanges(true);
      
      logger.debug('Personal info fact added', { fact });
    }
  }, [profileData.personal_info]);

  /**
   * Update a learned information fact
   * @param {number} index - Index of fact to update
   * @param {string} fact - New fact content
   */
  const updateLearnedInformationFact = useCallback((index, fact) => {
    const updatedFacts = [...profileData.learned_information];
    updatedFacts[index] = { ...updatedFacts[index], fact: fact.trim() };
    
    setProfileData(prev => ({
      ...prev,
      learned_information: updatedFacts
    }));
    
    sessionStorageService.setProfileFact('learned_information', updatedFacts);
    setHasUnsavedChanges(true);
    sessionStorageService.setProfileUnsavedChanges(true);
    
    logger.debug('Learned information fact updated', { index, fact });
  }, [profileData.learned_information]);

  /**
   * Remove a learned information fact
   * @param {number} index - Index of fact to remove
   */
  const removeLearnedInformationFact = useCallback((index) => {
    const updatedFacts = profileData.learned_information.filter((_, i) => i !== index);
    
    setProfileData(prev => ({
      ...prev,
      learned_information: updatedFacts
    }));
    
    sessionStorageService.setProfileFact('learned_information', updatedFacts);
    setHasUnsavedChanges(true);
    sessionStorageService.setProfileUnsavedChanges(true);
    
    logger.debug('Learned information fact removed', { index });
  }, [profileData.learned_information]);

  /**
   * Remove a personal info fact
   * @param {number} index - Index of fact to remove
   */
  const removePersonalInfoFact = useCallback((index) => {
    const updatedFacts = profileData.personal_info.filter((_, i) => i !== index);
    
    setProfileData(prev => ({
      ...prev,
      personal_info: updatedFacts
    }));
    
    sessionStorageService.setProfileFact('personal_info', updatedFacts);
    setHasUnsavedChanges(true);
    sessionStorageService.setProfileUnsavedChanges(true);
    
    logger.debug('Personal info fact removed', { index });
  }, [profileData.personal_info]);

  /**
   * Update a personal info fact
   * @param {number} index - Index of fact to update
   * @param {string} fact - New fact content
   */
  const updatePersonalInfoFact = useCallback((index, fact) => {
    const updatedFacts = [...profileData.personal_info];
    updatedFacts[index] = { ...updatedFacts[index], fact: fact.trim() };
    
    setProfileData(prev => ({
      ...prev,
      personal_info: updatedFacts
    }));
    
    sessionStorageService.setProfileFact('personal_info', updatedFacts);
    setHasUnsavedChanges(true);
    sessionStorageService.setProfileUnsavedChanges(true);
    
    logger.debug('Personal info fact updated', { index, fact });
  }, [profileData.personal_info]);

  // Load profile data on mount
  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  return {
    profileData,
    isEditing,
    hasUnsavedChanges,
    loading,
    error,
    successMessage,
    updateProfileFact,
    generateAddressOptions,
    loadProfileData,
    saveProfileChanges,
    undoChanges,
    addPersonalInfoFact,
    removePersonalInfoFact,
    updatePersonalInfoFact,
    updateLearnedInformationFact,
    removeLearnedInformationFact,
    setIsEditing,
    setError,
    setSuccessMessage
  };
};

export default useProfileData;
